/**
 * MobileTaskChat — WhatsApp-group-style chronological timeline for a task.
 *
 * Renders files, notes, and file-audit events as chat bubbles with
 * avatars + timestamps. Videos get a rich message tile: poster + play
 * overlay + duration + size + status pill + inline Approve/Disapprove.
 * Tap opens the full-screen MobileVideoPlayer.
 *
 * Rendered by TaskDialogContext when viewport <= md breakpoint. Desktop
 * layout is untouched.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { uploadChunked } from "@/lib/uploadChunked";
import { downloadFile } from "@/lib/download";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Play, Download, Upload, Send, CheckCircle2, XCircle,
  RotateCcw, Trash2, Undo2, FileUp, FilePenLine, MessageSquare, Loader2,
  FileText,
} from "lucide-react";
import MobileVideoPlayer from "./MobileVideoPlayer";

const VIDEO_EXTS = ["mp4","mov","webm","avi","mkv","m4v","3gp"];

function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const t = d.toTimeString().slice(0, 5);
  if (sameDay) return `Today at ${t}`;
  if (isYesterday) return `Yesterday at ${t}`;
  return `${d.getDate()}/${d.getMonth() + 1} ${t}`;
}

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

const AUDIT_META = {
  approved:            { label: "Approved",            tone: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  disapproved:         { label: "Disapproved",         tone: "bg-rose-100 text-rose-800 border-rose-200",           Icon: XCircle },
  revision_requested:  { label: "Revision requested",  tone: "bg-amber-100 text-amber-800 border-amber-200",        Icon: RotateCcw },
  uploaded:            { label: "Uploaded",            tone: "bg-sky-100 text-sky-800 border-sky-200",              Icon: FileUp },
  replaced:            { label: "Replaced",            tone: "bg-sky-100 text-sky-800 border-sky-200",              Icon: FileUp },
  renamed:             { label: "Renamed",             tone: "bg-slate-100 text-slate-800 border-slate-200",        Icon: FilePenLine },
  deleted:             { label: "Deleted",             tone: "bg-rose-100 text-rose-800 border-rose-200",           Icon: Trash2 },
  restored:            { label: "Restored",            tone: "bg-emerald-100 text-emerald-800 border-emerald-200",  Icon: Undo2 },
};

/** A user's bubble: avatar + name + timestamp + content column. */
function Bubble({ author, at, right = false, children }) {
  return (
    <div className={`flex items-start gap-2 my-2 ${right ? "flex-row-reverse" : ""}`}>
      <div className={`w-8 h-8 rounded-full ${right ? "bg-teal text-black" : "bg-purple-500 text-white"} flex items-center justify-center text-xs font-semibold shrink-0`}>
        {initials(author)}
      </div>
      <div className={`flex-1 min-w-0 ${right ? "items-end text-right" : ""} flex flex-col`}>
        <div className="text-[10px] text-muted-foreground mb-0.5">
          <span className="font-medium text-foreground">{author}</span>
          <span className="mx-1">·</span>
          {fmtTime(at)}
        </div>
        {children}
      </div>
    </div>
  );
}

function ReviewActions({ file, onDone }) {
  const [dialog, setDialog] = useState(null); // 'approve' | 'disapprove' | null
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (action) => {
    setBusy(true);
    try {
      await api.post(`/files/${file.id}/approve`, {
        action, comment: reason.trim() || (action === "approve" ? "Approved" : ""),
      });
      toast.success(action === "approve" ? "Approved" : "Revision requested");
      setDialog(null); setReason("");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };
  const status = file.approval_status;
  if (status === "approved" || status === "revision") {
    return (
      <div className="text-[10px] text-muted-foreground mt-1">
        {status === "approved" ? "You approved this" : "Revision requested"}
      </div>
    );
  }
  return (
    <>
      <div className="flex gap-2 mt-2">
        <button
          onClick={() => setDialog("approve")}
          className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-emerald-500 text-white text-xs font-medium active:bg-emerald-600"
          data-testid={`mob-file-approve-${file.id}`}
        >
          <CheckCircle2 size={14} /> Approve
        </button>
        <button
          onClick={() => setDialog("disapprove")}
          className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-rose-500 text-white text-xs font-medium active:bg-rose-600"
          data-testid={`mob-file-disapprove-${file.id}`}
        >
          <XCircle size={14} /> Disapprove
        </button>
      </div>
      {dialog && (
        <div className="fixed inset-0 z-[110] bg-black/60 flex items-end sm:items-center justify-center p-4" data-testid="mob-review-dialog">
          <div className="bg-background rounded-t-2xl sm:rounded-2xl w-full max-w-md p-4 shadow-2xl">
            <div className="text-sm font-semibold mb-2">
              {dialog === "approve" ? "Approve" : "Request changes"} — {file.original_filename}
            </div>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={dialog === "approve" ? "Comment (optional)" : "What needs to change? (required)"}
              className="w-full px-3 py-2 rounded border border-border bg-background text-sm"
              data-testid="mob-review-reason"
            />
            <div className="flex gap-2 mt-3">
              <button onClick={() => { setDialog(null); setReason(""); }}
                      className="flex-1 px-3 py-2 rounded border border-border text-sm">
                Cancel
              </button>
              <button
                disabled={busy || (dialog === "disapprove" && !reason.trim())}
                onClick={() => submit(dialog === "approve" ? "approve" : "disapprove")}
                className={`flex-1 px-3 py-2 rounded text-sm text-white font-medium ${
                  dialog === "approve" ? "bg-emerald-500 disabled:bg-emerald-500/40" : "bg-rose-500 disabled:bg-rose-500/40"
                }`}
                data-testid="mob-review-submit"
              >
                {busy ? <Loader2 className="inline animate-spin" size={14} /> : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function VideoTile({ file, canReview, onPlay, onRefresh, reasonNote }) {
  const [posterUrl, setPosterUrl] = useState(null);
  useEffect(() => {
    // Signed URL for the poster (same JWT dance as the player). Skipped
    // if playback isn't ready yet (poster is produced by the same job).
    if (file.playback_status !== "ready") return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/files/${file.id}/preview-url`);
        if (cancelled) return;
        const u = new URL(data.url, window.location.origin);
        const t = u.searchParams.get("t");
        if (t) setPosterUrl(`${API}/files/${file.id}/poster.jpg?t=${t}`);
      } catch { /* silent — poster is optional */ }
    })();
    return () => { cancelled = true; };
  }, [file.id, file.playback_status]);

  const statusPill = ({
    approved:  { label: "Approved",  tone: "bg-emerald-500" },
    revision:  { label: "Revision",  tone: "bg-amber-500" },
    pending:   { label: "Pending",   tone: "bg-slate-500" },
  })[file.approval_status] || null;

  const playbackPending = file.playback_status === "pending" || file.playback_status === "processing";
  const playbackFailed = file.playback_status === "failed";

  return (
    <div className="rounded-2xl overflow-hidden bg-muted/60 border border-border max-w-[85%]" data-testid={`mob-video-tile-${file.id}`}>
      {/* Thumbnail with play overlay */}
      <button
        onClick={() => !playbackPending && !playbackFailed && onPlay(file)}
        className="relative block w-full aspect-video bg-black active:opacity-80"
        data-testid={`mob-video-play-${file.id}`}
        disabled={playbackPending}
      >
        {posterUrl ? (
          <img src={posterUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-neutral-800 to-neutral-950" />
        )}
        {!playbackPending && !playbackFailed && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-black/60 backdrop-blur flex items-center justify-center">
              <Play size={28} className="text-white ml-1" fill="white" />
            </div>
          </div>
        )}
        {playbackPending && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 text-white text-xs gap-2">
            <Loader2 size={20} className="animate-spin" />
            Preparing preview…
          </div>
        )}
        {playbackFailed && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 text-white text-[11px] p-4 text-center">
            Preview unavailable — download to play.
          </div>
        )}
        {statusPill && (
          <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] text-white ${statusPill.tone}`}>
            {statusPill.label}
          </div>
        )}
      </button>

      {/* Meta strip */}
      <div className="px-3 py-2 flex items-center gap-2 text-[11px]">
        <FileText size={12} className="text-muted-foreground shrink-0" />
        <span className="flex-1 truncate">{file.original_filename}</span>
        <span className="text-muted-foreground shrink-0">{fmtSize(file.size)}</span>
        <button
          onClick={() => downloadFile(`${API}/files/${file.id}/download`, file.original_filename)}
          className="p-1.5 rounded-full hover:bg-muted active:bg-muted-foreground/10"
          aria-label="Download"
          data-testid={`mob-video-download-${file.id}`}
        >
          <Download size={14} />
        </button>
      </div>

      {/* Reviewer's reason directly below the video (user's explicit ask) */}
      {reasonNote && (
        <div className={`px-3 py-2 border-t border-border ${reasonNote.action === "disapproved" ? "bg-rose-50 dark:bg-rose-950/30" : "bg-emerald-50 dark:bg-emerald-950/30"}`}>
          <div className="text-[10px] font-semibold uppercase tracking-wider mb-0.5">
            {reasonNote.action === "disapproved" ? "Disapproved" : "Reviewer note"}
            <span className="ml-2 text-muted-foreground normal-case tracking-normal font-normal">
              by {reasonNote.by_name}
            </span>
          </div>
          <div className="text-xs whitespace-pre-wrap">{reasonNote.text}</div>
        </div>
      )}

      {canReview && !playbackPending && (
        <div className="px-3 pb-3">
          <ReviewActions file={file} onDone={onRefresh} />
        </div>
      )}
    </div>
  );
}

function FileTile({ file }) {
  return (
    <a
      href={`${API}/files/${file.id}/download`}
      onClick={(e) => { e.preventDefault(); downloadFile(`${API}/files/${file.id}/download`, file.original_filename); }}
      className="flex items-center gap-2 rounded-2xl bg-muted/60 border border-border p-3 max-w-[85%] active:bg-muted"
      data-testid={`mob-file-tile-${file.id}`}
    >
      <FileText size={18} className="text-teal shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{file.original_filename}</div>
        <div className="text-[10px] text-muted-foreground">{fmtSize(file.size)}</div>
      </div>
      <Download size={16} className="text-muted-foreground" />
    </a>
  );
}

function AuditRow({ note }) {
  const meta = AUDIT_META[note.action] || { label: note.action, tone: "bg-slate-100 text-slate-800 border-slate-200", Icon: FilePenLine };
  const { Icon } = meta;
  return (
    <div className="mx-auto max-w-[90%] my-2 text-center" data-testid={`mob-audit-${note.id}`}>
      <div className={`inline-flex flex-col gap-1 border rounded-2xl px-3 py-2 text-[11px] ${meta.tone}`}>
        <div className="flex items-center justify-center gap-1.5">
          <Icon size={12} />
          <span className="font-semibold">{meta.label}</span>
          {note.file_name && <span className="opacity-70 truncate max-w-[180px]">· {note.file_name}</span>}
        </div>
        <div className="opacity-70">{note.by_name} · {fmtTime(note.at)}</div>
        {note.text && <div className="mt-1 whitespace-pre-wrap text-left px-1">{note.text}</div>}
      </div>
    </div>
  );
}

export default function MobileTaskChat({ taskId, onClose }) {
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [files, setFiles] = useState([]);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadPhase, setUploadPhase] = useState("");
  const [playFile, setPlayFile] = useState(null);
  const listEndRef = useRef(null);

  const isAdminish = ["super_admin", "admin", "manager", "account_manager", "art_director"].includes(user?.role);

  const refresh = async () => {
    if (!taskId) return;
    try {
      const [{ data: t }, { data: notes }] = await Promise.all([
        api.get(`/tasks/${taskId}`),
        api.get(`/tasks/${taskId}/notes`).catch(() => ({ data: [] })),
      ]);
      setTask(t);
      setComments(Array.isArray(notes) ? notes : []);
      if ((t.files || []).length) {
        const { data: fs } = await api.get(`/files`, { params: { client_id: t.client_id } });
        setFiles((fs || []).filter((f) => t.files.includes(f.id)));
      } else {
        setFiles([]);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Load failed");
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [taskId]);

  // Auto-scroll to newest item after data loads.
  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments.length, files.length]);

  // Poll every 6s so a reviewer's action appears without manual refresh.
  useEffect(() => {
    if (!taskId) return undefined;
    const iv = setInterval(refresh, 6000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  // For each file, find the most-recent disapproval / approval note that
  // references it — we display that reason directly under the video.
  // Prefer matching by `file_id` when the audit note carries it (Iter153
  // extra); fall back to `file_name` for older notes that pre-date that
  // field. Prevents cross-contamination when two files share a name.
  const reasonByFile = useMemo(() => {
    const byId = {};
    const byName = {};
    for (const c of comments) {
      if (c.kind !== "file_audit") continue;
      if (!["disapproved", "approved", "revision_requested"].includes(c.action)) continue;
      if (c.file_id) byId[c.file_id] = c;
      else if (c.file_name) byName[c.file_name] = c;   // legacy fallback
    }
    return { byId, byName };
  }, [comments]);

  const timeline = useMemo(() => {
    const items = [];
    // Files as bubbles (chronological by created_at)
    for (const f of files) {
      items.push({ kind: "file", at: f.created_at, data: f });
    }
    // Comments: file_audit rows become audit chips; user notes become bubbles
    for (const c of comments) {
      items.push({ kind: c.kind === "file_audit" ? "audit" : "note", at: c.at, data: c });
    }
    items.sort((a, b) => String(a.at).localeCompare(String(b.at)));
    return items;
  }, [files, comments]);

  const postNote = async () => {
    const t = text.trim();
    if (!t) return;
    try {
      const { data } = await api.post(`/tasks/${taskId}/note`, { text: t });
      setComments((prev) => [...prev, data.note]);
      setText("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !task) return;
    setUploading(true); setUploadPct(0); setUploadPhase("hashing");
    try {
      await uploadChunked(f, {
        client_id: task.client_id,
        task_id: task.id,
        onProgress: ({ pct, phase }) => { setUploadPct(pct); setUploadPhase(phase || "uploading"); },
      });
      toast.success(`Uploaded ${f.name}`);
      e.target.value = "";
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); setUploadPct(0); setUploadPhase(""); }
  };

  if (!task) {
    return (
      <div className="fixed inset-0 z-50 bg-background flex items-center justify-center" data-testid="mob-task-chat-loading">
        <Loader2 className="animate-spin text-teal" size={22} />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col" data-testid="mob-task-chat">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-3 border-b border-border bg-background">
        <button onClick={onClose} className="p-1.5 -ml-1.5 rounded-full active:bg-muted" data-testid="mob-task-close">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">{task.title}</div>
          <div className="text-[10px] text-muted-foreground truncate">
            {task.client_name} · {(task.status || "").replace(/_/g, " ")}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto px-3 py-2" data-testid="mob-task-timeline">
        {task.description && (
          <div className="mx-auto max-w-[90%] my-2 text-center text-[11px] text-muted-foreground bg-muted/40 rounded-2xl px-3 py-2">
            <div className="uppercase tracking-wider mb-0.5">Brief</div>
            <div className="whitespace-pre-wrap text-left">{task.description}</div>
          </div>
        )}
        {timeline.length === 0 && (
          <div className="text-center text-xs text-muted-foreground py-8">
            No messages yet. Upload a file or drop a note.
          </div>
        )}
        {timeline.map((item, idx) => {
          if (item.kind === "file") {
            const f = item.data;
            const ext = (f.original_filename || "").toLowerCase().split(".").pop();
            const isVideo = VIDEO_EXTS.includes(ext) || (f.content_type || "").startsWith("video/");
            const reason = reasonByFile.byId[f.id] || reasonByFile.byName[f.original_filename];
            return (
              <Bubble key={`file-${f.id}`} author={f.uploader_name || "Someone"} at={f.created_at}
                      right={f.uploader_id === user?.id}>
                {isVideo ? (
                  <VideoTile file={f} canReview={isAdminish && f.approval_status !== "approved" && f.approval_status !== "revision"}
                             onPlay={setPlayFile} onRefresh={refresh} reasonNote={reason} />
                ) : (
                  <FileTile file={f} />
                )}
              </Bubble>
            );
          }
          if (item.kind === "audit") {
            return <AuditRow key={`aud-${item.data.id || idx}`} note={item.data} />;
          }
          const c = item.data;
          return (
            <Bubble key={`note-${c.id || idx}`} author={c.by_name || "Someone"} at={c.at}
                    right={c.by_id === user?.id}>
              <div className="rounded-2xl bg-teal/10 border border-teal/20 px-3 py-2 text-sm whitespace-pre-wrap max-w-[85%]">
                {c.text}
              </div>
            </Bubble>
          );
        })}
        <div ref={listEndRef} />
      </div>

      {/* Composer */}
      <div className="px-3 py-2 border-t border-border bg-background" data-testid="mob-task-composer">
        {uploading && (
          <div className="mb-2">
            <div className="h-1 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-teal transition-all" style={{ width: `${uploadPct}%` }} />
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              {uploadPhase === "hashing" ? "Verifying integrity" : "Uploading"} · {uploadPct}%
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          <label className={`p-2 rounded-full active:bg-muted ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
            <input type="file" onChange={upload} className="hidden" data-testid="mob-task-upload" />
            <Upload size={20} className="text-teal" />
          </label>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); postNote(); } }}
            placeholder="Message"
            className="flex-1 px-3 py-2 rounded-full border border-border bg-muted/40 text-sm"
            data-testid="mob-task-input"
          />
          <button onClick={postNote} disabled={!text.trim()}
                  className="p-2 rounded-full bg-teal text-black disabled:opacity-40"
                  data-testid="mob-task-send">
            <Send size={18} />
          </button>
        </div>
      </div>

      <MobileVideoPlayer file={playFile} open={!!playFile} onClose={() => setPlayFile(null)} />
    </div>
  );
}
